import { apiClient } from "../../lib/apiClient";

export interface RotateResponse {
  api_key_plaintext: string;
}

/**
 * Read-state describing the *current* API key for a project. Returned by
 * GET /api/admin/projects/:id/api-keys. The plaintext is always retrievable
 * by an authenticated admin so it can be re-copied at any time.
 *
 * `last4`, `fingerprint` and `rotated_at` are null for never-rotated keys.
 * `rotated_by` is null when the key was never rotated (and its `email` can be
 * null if that admin account was since deleted).
 */
export interface ApiKeyState {
  /** Full plaintext API key — used by the Unity SDK / backend integrations. */
  api_key_plaintext: string;
  /** Last 4 chars of the plaintext — null for never-rotated keys. */
  last4: string | null;
  /** SHA-256 hex digest (64 hex chars) — null for never-rotated keys. */
  fingerprint: string | null;
  /** When the current key was created (ISO 8601). */
  created_at: string;
  /** When the current key was last rotated (ISO 8601) — null if never. */
  rotated_at: string | null;
  /** The admin who last rotated the key — null if never rotated. */
  rotated_by: { id: string; email: string | null } | null;
}

export async function getApiKey(projectId: string): Promise<ApiKeyState> {
  const r = await apiClient.get<ApiKeyState>(
    `/api/admin/projects/${encodeURIComponent(projectId)}/api-keys`,
  );
  return r.data;
}

export async function rotateApiKey(projectId: string): Promise<RotateResponse> {
  const r = await apiClient.post<RotateResponse>(
    `/api/admin/projects/${encodeURIComponent(projectId)}/api-key/rotate`,
  );
  return r.data;
}
